import React, { useEffect, useState } from "react";
import { IChoiceGroupOption, IComboBoxOption } from "@fluentui/react";
import { FormTreeSelection } from "../../cpm/FormTree";
import { FormTable, SheetForm, TgkTableObjectSource } from "../../interfaces/CoObject";
import { ObjectPanelTabs } from "../co-panel/ObjectPanelComponent";
import { CheckboxChangeEvent } from "@progress/kendo-react-inputs";
import { SourceTypeEnum } from "../../../api/models/source-type-enum";
import { CoObjectSourcePanelStateData } from "./CoObjectSourceTablePanel";
import { tableSourceTypeEnumOptions } from "../SourceTypeEnumOptions";

const sheetsAreaChartMap = new Map<string, string[]>();

const initialStateData: CoObjectSourcePanelStateData = {
  areasItems: [],
  loading: true,
};

const useCoObjectSourceTablePanel = (props) => {
  const [stateData, setStateData] = React.useState<CoObjectSourcePanelStateData>(initialStateData);
  const [isOldValueForAreaToHide, setIsOldValueForAreaToHide] = useState<boolean>(true);
  const [hasSheetToBeSelected, setHasSheetToBeSelected] = useState<boolean>(false);
  const [isLoadingDataExcel, setIsLoadingDataExcel] = useState<boolean>(false);
  const [isImageUnchecked, setIsImageUnchecked] = useState<boolean>(false);

  const {
    objectSource,
    updateCoObjectSourceTable,
    isFileExcelExisting,
    fileExcelName,
    updateFileExcelName,
    isEdit,
    isComponentInstance,
    isLocked,
  } = props;
  const tableType = props.tableType ?? "TABLE";

  useEffect(() => {
    const objSource =
      objectSource == null
        ? ({
            //DEFAULT OBJSOURCE
            type: tableType,
            objectSource: "FORM",
            dbApplication: null,
            form: null,
            sheet: null,
            area: null,
            path: null,
            elabResultType: tableType == "CHART" ? "IMAGE_STRINGBASE64" : "STRINGBASE64",
            inheritFormatImage: true,
            imageExtension: null,
            imageResolution: null,
          } as TgkTableObjectSource)
        : ({ ...objectSource, type: tableType } as TgkTableObjectSource);

    updateCoObjectSourceTable(objSource, validateFields(objSource), false);
  }, []);

  function initAreasOrCharts(sheet: SheetForm) {
    if (sheetsAreaChartMap) {
      sheetsAreaChartMap.forEach((value: string[], key: string) => {
        if (sheet && sheet.code == key) {
          const areaMap: IComboBoxOption[] = value?.map((area) => {
            return createIComboBoxFromAreas(area);
          });
          setStateData({
            ...stateData,
            areasItems: areaMap,
          });
        }
      });
    }
  }

  function createIComboBoxFromAreas(areaValue: string): IComboBoxOption {
    return {
      key: areaValue,
      text: areaValue,
    };
  }

  function selectDbComboBox(option: IComboBoxOption) {
    const obj = {
      ...objectSource,
      dbApplication: { id: String(option.key), desc: option.text },
      form: null,
      sheet: null,
      area: null,
    } as TgkTableObjectSource;
    updateCoObjectSourceTable(obj, validateFields(obj), true);
  }

  const onFormClick = (formSelection: FormTreeSelection) => {
    sheetsAreaChartMap.clear();
    const formTable = {
      code: formSelection.formCode,
      desc: formSelection.formDesc,
      oid: formSelection.key,
      oidPath: formSelection.oidPath,
    } as FormTable;

    const obj = {
      ...objectSource,
      form: formTable,
      sheet: null,
      area: null,
      path: formSelection.oidPath,
    } as TgkTableObjectSource;

    updateCoObjectSourceTable(obj, validateFields(obj), true);
  };

  const onFileClick = (formSelection: FormTreeSelection) => {
    sheetsAreaChartMap.clear();
    const formTable = {
      code: formSelection.formCode,
      desc: formSelection.formDesc,
      oid: formSelection.key,
      oidPath: formSelection.oidPath,
    } as FormTable;

    const obj = {
      ...objectSource,
      form: formTable,
      sheet: null,
      area: null,
      path: formSelection.oidPath,
    } as TgkTableObjectSource;

    updateCoObjectSourceTable(obj, validateFields(obj), true);
  };

  function selectSheetComboBox(option: IComboBoxOption) {
    const obj = {
      ...objectSource,
      sheet: { code: String(option.key), desc: option.text, uid: null },
      area: null,
      chart: null,
    } as TgkTableObjectSource;

    updateCoObjectSourceTable(obj, validateFields(obj), true);
    initAreasOrCharts({ code: String(option.key), desc: option.text } as SheetForm);
  }

  function selectAreaComboBox(option: IComboBoxOption) {
    const obj = { ...objectSource, area: { name: String(option.key) } } as TgkTableObjectSource;
    updateCoObjectSourceTable(obj, validateFields(obj), true);
  }

  function selectChartComboBox(option: IComboBoxOption) {
    const obj = { ...objectSource, chart: { name: String(option.key) } } as TgkTableObjectSource;
    updateCoObjectSourceTable(obj, validateFields(obj), true);
  }

  function onToggleInheritableImageFormat(
    objectType,
    inheritFormatImage,
    imageExtension,
    imageResolution,
    modified = true
  ) {
    const obj = {
      ...objectSource,
      type: objectType,
      inheritFormatImage: inheritFormatImage,
      imageExtension: imageExtension,
      imageResolution: imageResolution,
    } as TgkTableObjectSource;
    updateCoObjectSourceTable(obj, validateFields(obj), modified);
  }

  function handleChangeImageExtension(selectedKey, modified) {
    const obj = {
      ...objectSource,
      imageExtension: selectedKey,
    } as TgkTableObjectSource;
    updateCoObjectSourceTable(obj, validateFields(obj), modified);
  }

  function handleChangeImageResolution(selectedKey, modified) {
    const obj = {
      ...objectSource,
      imageResolution: selectedKey,
    } as TgkTableObjectSource;
    updateCoObjectSourceTable(obj, validateFields(obj), modified);
  }

  function validateFields(obj: TgkTableObjectSource): ObjectPanelTabs {
    let isValid = true;
    if (obj == null) isValid = false;
    else {
      const { dbApplication, form, sheet, area, chart } = obj;
      if (obj.objectSource !== SourceTypeEnum.EXCEL) {
        if (!dbApplication || !dbApplication.id) {
          isValid = false;
        }
      }
      if (!form?.oid || !sheet?.desc || (!area?.name && !chart?.name)) {
        isValid = false;
      }
    }
    return { key: props.tab.key, name: props.tab.name, isValid: isValid };
  }

  function onFormatAsImageChange(event: CheckboxChangeEvent) {
    const obj = {
      ...objectSource,
      elabResultType: event.value ? "IMAGE_STRINGBASE64" : "STRINGBASE64",
    } as TgkTableObjectSource;
    updateCoObjectSourceTable(obj, validateFields(obj), true);
    if (props.isEdit) {
      if (!event.value) {
        setIsImageUnchecked(true);
      } else {
        setIsImageUnchecked(false);
      }
    }
  }

  const checkboxStyles = {
    marginRight: "5px",
  };

  const checkboxLabelStyles = {
    marginTop: "20px",
  };

  const checkboxLabelStylesDisabled = {
    marginTop: "20px",
    color: "rgb(161, 159, 157)",
  };

  const onChangeFormatAs = (_: React.SyntheticEvent<HTMLElement>, option: IChoiceGroupOption) => {
    const obj = {
      type: tableType,
      objectSource: String(option.key),
      dbApplication: null,
      form: null,
      sheet: null,
      area: null,
      path: null,
      elabResultType: tableType == "CHART" ? "IMAGE_STRINGBASE64" : "STRINGBASE64",
      inheritFormatImage: true,
      imageExtension: null,
      imageResolution: null,
    } as TgkTableObjectSource;
    // eslint-disable-next-line no-undef
    console.log("###CHANGING SOURCE FORM: " + String(option.key));
    updateCoObjectSourceTable(obj, validateFields(obj), true);
  };

  return {
    objectSource,
    updateCoObjectSourceTable,
    tableType,
    sheetsAreaChartMap,
    tableSourceTypeEnumOptions,
    onChangeFormatAs,
    onFormClick,
    selectDbComboBox,
    onFileClick,
    selectSheetComboBox,
    initAreasOrCharts,
    selectAreaComboBox,
    selectChartComboBox,
    onFormatAsImageChange,
    checkboxStyles,
    checkboxLabelStyles,
    onToggleInheritableImageFormat,
    handleChangeImageExtension,
    handleChangeImageResolution,
    isFileExcelExisting,
    fileExcelName,
    updateFileExcelName,
    checkboxLabelStylesDisabled,
    isEdit,
    isComponentInstance,
    isLocked,
    isOldValueForAreaToHide,
    setIsOldValueForAreaToHide,
    hasSheetToBeSelected,
    setHasSheetToBeSelected,
    isLoadingDataExcel,
    setIsLoadingDataExcel,
    isImageUnchecked,
  };
};

export default useCoObjectSourceTablePanel;
